import { useEffect, useRef } from "react";

import { useQuery } from "@tanstack/react-query";
import { renderInlineBold } from "@recruiter/shared";

import { SellerQueryError, threadDetailQueryOptions } from "../../lib/queries/seller";
import { useSellerStore } from "../../lib/store/seller-store";
import { sellerDocUrl } from "../../lib/seller-client";
import { DocIcon, MiraMarkIcon, PaperclipIcon } from "../talent/icons";
import { DocExportButton } from "./doc-export-button";
import { ReplyInput } from "./reply-input";
import { SellerErrorState } from "./seller-error-state";

function clock(iso: string): string {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return "";
  return at.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

// One project's conversation with Mira (STERLING), the middle column under the project
// header. Server state via
// TanStack Query (keyed by talent + thread): opening a thread fetches it; a `seller.message`
// event is appended to this cache entry by the dispatcher (no re-GET), and a
// `seller.thread.update` invalidates it so the authoritative offer re-loads. Switching
// threads re-keys the query, so the loading state shows automatically.
export function ThreadView({ threadId }: { threadId: string }) {
  const sellerId = useSellerStore((s) => s.activeSellerId);
  const {
    data: thread,
    isPending,
    error,
    refetch,
  } = useQuery(threadDetailQueryOptions(sellerId, threadId));
  const list = useRef<HTMLDivElement | null>(null);

  // Scroll to the newest message: fires when the thread loads and whenever the
  // dispatcher appends a pushed message (the cached messages array grows). The LIST
  // scrolls, not the page: `scrollIntoView` would also scroll the middle column and push
  // the project header out of sight on every new message.
  const messageCount = thread?.messages.length ?? 0;
  useEffect(() => {
    const el = list.current;
    el?.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
  }, [messageCount]);

  const head = (
    <header className="sl-convo__head">
      <MiraMarkIcon size={34} className="sl-mira-mark" />
      <div className="sl-convo__heading">
        <h2 className="sl-convo__title">Conversation with Mira</h2>
      </div>
    </header>
  );

  if (isPending) {
    return (
      <section className="sl-convo" aria-label="Conversation with Mira">
        {head}
        <div className="sl-convo__scroll">
          <span className="sl-skel sl-skel--bubble" />
          <span className="sl-skel sl-skel--bubble sl-skel--mine" />
        </div>
      </section>
    );
  }
  if (error) {
    return (
      <section className="sl-convo" aria-label="Conversation with Mira">
        {head}
        <SellerErrorState
          title="Couldn't load the conversation"
          message={error instanceof SellerQueryError ? error.message : "Something went wrong."}
          onRetry={() => void refetch()}
        />
      </section>
    );
  }
  if (!thread) return null;

  return (
    <section className="sl-convo" aria-label="Conversation with Mira">
      {head}
      <div className="sl-convo__scroll" ref={list}>
        {thread.messages.length === 0 ? (
          <p className="sl-hint sl-convo__empty">Mira's first message will appear here.</p>
        ) : null}
        {thread.messages.map((m) => {
          // Who wrote it: Mira, the client (after they took the conversation over), or
          // the talent. A line Pusher appended reads as Mira until the next GET relabels
          // it (the event cannot tell the client apart from Mira).
          const author = m.author;
          const who =
            author === "talent" ? "You" : author === "client" ? thread.client_name : "Mira";
          const docs = m.documents ?? [];
          const attachments = m.attachments ?? [];
          return (
            <div key={m.id} className={`sl-msg sl-msg--${author}`}>
              <div className="sl-msg__who">
                {who}
                <span className="sl-msg__time">{clock(m.sent_at)}</span>
              </div>
              <div className="sl-msg__body">{renderInlineBold(m.body)}</div>
              {docs.length > 0 ? (
                <div className="sl-msg__docs">
                  {docs.map((d) => (
                    <span key={d.kind} className="sl-doc">
                      <a
                        className="sl-doc__view"
                        href={sellerDocUrl(d.view_url)}
                        target="_blank"
                        rel="noreferrer"
                      >
                        <DocIcon size={14} /> {d.label}
                      </a>
                      <DocExportButton
                        threadId={threadId}
                        kind={d.kind}
                        sellerId={sellerId}
                        exportUrl={d.export_url}
                      />
                    </span>
                  ))}
                </div>
              ) : null}
              {attachments.length > 0 ? (
                <div className="sl-msg__docs">
                  {attachments.map((a) => {
                    const scanning = a.scan_status !== "clean";
                    const note = scanning
                      ? "scanning…"
                      : a.status === "failed"
                        ? "couldn't read"
                        : a.status !== "ready"
                          ? "reading…"
                          : null;
                    return (
                      <span key={a.id} className="sl-doc">
                        {a.scan_status === "clean" ? (
                          <a
                            className="sl-doc__view"
                            href={sellerDocUrl(a.content_url)}
                            target="_blank"
                            rel="noreferrer"
                          >
                            <PaperclipIcon size={14} /> {a.file_name}
                          </a>
                        ) : (
                          <span className="sl-doc__view">
                            <PaperclipIcon size={14} /> {a.file_name}
                          </span>
                        )}
                        {note ? (
                          <span className="sl-doc__pdf" aria-hidden="true">
                            {note}
                          </span>
                        ) : null}
                      </span>
                    );
                  })}
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
      {/* key={threadId}: a fresh composer (cleared draft) per thread. */}
      <ReplyInput key={threadId} threadId={threadId} sellerId={sellerId} />
    </section>
  );
}
